import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { TOKENS } from '@trustedshops/tswp-core-authorization';
import {
  IdentityInitializer,
  TswpCoreAuthorizationModule,
} from '@trustedshops/tswp-core-authorization-implementation';
import {
  AngularKeycloakHttpInterceptor,
  KeycloakSessionProviderService,
  SetUpKeycloakDataInterceptor,
  SetUpLoginUiRedirectInterceptor,
  TswpCoreAuthorizationKeycloakModule,
} from '@trustedshops/tswp-core-authorization-keycloak-implementation';
import { TOKENS as TOKENS_COMMON } from '@trustedshops/tswp-core-common';
import {
  InstanaLoggingEarlyInitializer,
  InstanaLoggingLateInitializer,
  TswpCoreCommonDiagInstanaModule,
} from '@trustedshops/tswp-core-common-diag-instana';
import {
  ApplicationDataInitializer,
  CssResourceLoaderInitializer,
  JsResourceLoaderInitializer,
  TswpCoreCommonModule,
} from '@trustedshops/tswp-core-common-implementation';
import { TswpCoreCompositionInversifyModule } from '@trustedshops/tswp-core-composition-inversify';
import { TswpCoreMasterdataModule } from '@trustedshops/tswp-core-masterdata-implementation';
import {
  BrowserHostedPluginSystemModule,
  BrowserPluginHostInitializer,
  TswpCorePluginsModule,
} from '@trustedshops/tswp-core-plugins-implementation';
import {
  TswpCoreUiModule,
  UserInteractionRendererInitializer,
} from '@trustedshops/tswp-core-ui-implementation';
import { AnimationConnectionInitializer } from './bootstrappers/animation-connection.bootstrapper';
import { DiagnosticsInitializer } from './bootstrappers/diagnostics.bootstrapper';
import { ExtraOrdinaryServicesInitializer } from './bootstrappers/extra-ordinary-services.bootstrapper';
import { GlobalizationInitializer } from './bootstrappers/globalization.bootstrapper';
import { NavigationInitializer } from './bootstrappers/navigation.bootstrapper';
import { ProtectedUrlsInitializer } from './bootstrappers/protected-urls.bootstrapper';
import { StartPluginInitializer } from './bootstrappers/start-plugins.bootstrapper';
import { ErrorComponent } from './components/error/error.component';
import { CarrierConfig } from './models/carrier-config.interface';
import { CarrierConfigToken } from './models/carrier-config.token';
import { CurrentCultureInfoProviderImpl } from './services/culture-info-provider.service';
import { ErrorService } from './services/error.service';
import { WebPlatformHttpInterceptor } from './services/non-angular-http-interceptor-translator';

@NgModule({
  declarations: [
    ErrorComponent,
  ],
  imports: [
    TswpCoreCommonModule,
    TswpCorePluginsModule,
    TswpCoreCompositionInversifyModule,
    TswpCoreUiModule,
    TswpCoreAuthorizationModule,
    TswpCoreMasterdataModule,
    BrowserHostedPluginSystemModule,
    TswpCoreAuthorizationKeycloakModule,
    TswpCoreCommonDiagInstanaModule,
  ],
  exports: [
    TswpCoreCommonModule,
    TswpCorePluginsModule,
    TswpCoreCompositionInversifyModule,
    TswpCoreUiModule,
    TswpCoreMasterdataModule,
    TswpCoreAuthorizationModule,

    BrowserHostedPluginSystemModule,
    TswpCoreAuthorizationKeycloakModule,
    ErrorComponent,
  ],
  providers: [
    DiagnosticsInitializer,

    NavigationInitializer,

    {
      provide: TOKENS_COMMON.CurrentCultureInfoProvider,
      useClass: CurrentCultureInfoProviderImpl,
    },
    GlobalizationInitializer,

    ProtectedUrlsInitializer,
    AngularKeycloakHttpInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WebPlatformHttpInterceptor,
      multi: true,
    },
    SetUpLoginUiRedirectInterceptor,
    SetUpKeycloakDataInterceptor,

    AnimationConnectionInitializer,
    ExtraOrdinaryServicesInitializer,
    JsResourceLoaderInitializer,
    CssResourceLoaderInitializer,
    StartPluginInitializer,
    BrowserPluginHostInitializer,

    UserInteractionRendererInitializer,

    ErrorService,
  ],
})
export class TswpCarrierDefaultConfigModule {
  public static withConfig(config: CarrierConfig): ModuleWithProviders<unknown> {
    return {
      ngModule: TswpCarrierDefaultConfigModule,
      providers: [
        { provide: CarrierConfigToken, useValue: config },

        // Early Instana initialization - runs FIRST, no dependencies
        InstanaLoggingEarlyInitializer(config.diagnostics.logging.instana),

        ApplicationDataInitializer(config.environment),

        IdentityInitializer(
          KeycloakSessionProviderService as any,
          config.authorization.sessionProviderConfig.initializationOptions,
        ),
        {
          provide: TOKENS.SessionProviderConfiguration,
          useValue: config.authorization.sessionProviderConfig,
        },

        // Late Instana initialization - runs AFTER dependencies are ready
        InstanaLoggingLateInitializer(config.diagnostics.logging.instana),
      ],
    };
  }
}
